import type { ReactNode } from "react";

import { FaultLinkPanel } from "../components/FaultLinkPanel";
import { LoadLinkPanel } from "../components/LoadLinkPanel";
import { PfCorrectionForm } from "../components/PfCorrectionForm";
import { PfCorrectionResultPanel } from "../components/PfCorrectionResultPanel";
import { PfCorrectionResultSummary } from "../components/PfCorrectionResultSummary";
import { PowerQualityWarningPanel } from "../components/PowerQualityWarningPanel";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { usePfCorrection } from "../hooks/usePfCorrection";
import { useSldElements } from "../hooks/useSldElements";
import type { PfCorrectionRunCreateRequest } from "../services/pfCorrection";
import { downloadRunJson } from "../services/runExport";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The power factor correction study could not be completed.";
}

type PfCorrectionPageProps = {
  // The switch between the two power quality studies; the host page puts it in the header.
  // Without it the page is exactly the single page it was.
  modePicker?: ReactNode;
};

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside a result-first
// column - summary, the three links (SLD, load run, fault run), warnings, traceability, then
// the detail tables. EOS-11 is delivered in two parts (A32); this page is 11a.
export function PfCorrectionPage({ modePicker }: PfCorrectionPageProps = {}) {
  const { calculate, reset, result, run, error, isPending, isError } = usePfCorrection();
  // The active revision's single-line diagram, for the bus picker (A27 (b)).
  const { elements: sldElements } = useSldElements();

  async function handleSubmit(payload: PfCorrectionRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-pf-correction-page>
      <header>
        <p>EOS-11 · Power factor and harmonics</p>
        <h1>Power factor correction</h1>
        <p>
          Size the reactive compensation of one bus from its demand (a stored load study or
          typed figures) to a target power factor, state the capacitor bank rating at its
          declared voltage with or without a detuned reactor, and screen the parallel resonance
          with the source against the characteristic harmonic orders present in the loads,
          using the short-circuit power of a stored fault study of the same project revision.
          No tariff figure is stated.
        </p>
        {modePicker}
      </header>

      <div data-study-layout>
        <section aria-labelledby="pf-correction-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="pf-correction-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <PfCorrectionForm disabled={isPending} onSubmit={handleSubmit} sldElements={sldElements} />
          </details>
        </section>

        <section aria-labelledby="pf-correction-results-heading" data-study-results>
          <h2 id="pf-correction-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Sizing the compensation…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <PfCorrectionResultSummary result={result} />
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              {run ? <LoadLinkPanel link={run.load_link} /> : null}
              {run ? <FaultLinkPanel link={run.fault_link} /> : null}
              <PowerQualityWarningPanel warnings={result.warnings} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <PfCorrectionResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">Submit the inputs to size the compensation.</p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="pf-correction-review-heading">
        <h2 id="pf-correction-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance
          declaration. Confirm the linked load and fault studies, the target power factor
          against the supply's conditions, the capacitor rated voltage against the registered
          capacitor rules and the resonance screening before approval or issue. The billing
          context is information only until a controlled copy of the tariff order is
          registered.
        </p>
      </section>
    </main>
  );
}

export default PfCorrectionPage;
